import { type MouseEvent, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Return } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Bar } from './bar';
import { Palette } from './palette';
import { moved } from './screen';
import { Sections } from './sections';

/**
 * The frame every signed-in screen sits in: the family bar, which is the same in every product, and
 * under it this interface's own sections on the shared `Sidebar` (D53) beside the screen's `Page`
 * (D52), which each screen draws through `Screen`: edge to edge, its content one gutter after the
 * sidebar, never centered.
 *
 * Personal settings, organization administration and leaving for a product stay apart: the sidebar
 * holds the first two, and the family bar's product menu and the validated way back, the arrival line
 * at the top of the page (D54), hold the third. The organization in view is always named in the bar,
 * so nobody administers the wrong tenant by accident. The language and appearance are chosen on "Your
 * account", which applies them at once for all of Beyond; this interface's own bar offers no
 * "Language and appearance" of its own.
 *
 * A product may send a person to an organization screen, or to "Your account" to change their
 * language and appearance for all of Beyond, and ask for them back, as
 * `?product=<id>&return=<address>`. The request is taken out of the address at once and handed to be
 * validated; what stays visible is the way back the service accepted, or nothing.
 */
export function Shell() {
	const { text, arrive } = usePresence();
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		const parameters = new URLSearchParams(location.search);
		const returnable = location.pathname.startsWith('/organizations') || location.pathname === '/account';
		if (!returnable || !Return.requested(parameters)) return;

		void arrive(parameters);
		// Removed straight away, so the address that arrived is never bookmarked, shared or read again.
		// Everything else in the query, such as the view a product asked for, stays
		const rest = new URLSearchParams(parameters);
		rest.delete(Return.PRODUCT);
		rest.delete(Return.ADDRESS);
		const query = rest.toString();
		navigate({ pathname: location.pathname, search: query ? `?${query}` : '', hash: location.hash }, { replace: true, state: location.state });
	}, [arrive, location, navigate]);

	/**
	 * The family's components draw this interface's own addresses as plain links (a `ListRow`'s name, an
	 * empty state's action, a checklist step). A plain click on one is followed here without reloading
	 * the application; a modified click, another window's target, a download or an address elsewhere is
	 * left to the browser, and so is a link that already handled the click (React Router's own).
	 */
	function follow(event: MouseEvent<HTMLElement>) {
		if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
		const anchor = (event.target as Element | null)?.closest?.('a[href]');
		const href = anchor?.getAttribute('href');
		if (!anchor || !href || !href.startsWith('/') || href.startsWith('//') || anchor.hasAttribute('download') || (anchor.getAttribute('target') ?? '_self') !== '_self') return;
		event.preventDefault();
		navigate(href, { state: moved });
	}

	return (
		<div className="min-h-screen bg-canvas" onClick={follow}>
			<a
				href="#main"
				className="skip sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-beyond focus:bg-raised focus:text-ink"
			>
				{text('nav.skip')}
			</a>

			<Bar />
			<Palette />
			<div className="bui-shell">
				<Sections />
				<main id="main" className="flex min-w-0">
					<Outlet />
				</main>
			</div>
		</div>
	);
}
